import _ from 'underscore';

import * as analytics from 'analytics/Analytics';
import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {SequencePrimerAttributes} from 'biojs/SequencePrimer';
import {circularArraySlice, NucleotideType} from 'biojs/Util';
import {Sequence as ComponentSequence} from 'components/Sequence';
import {Strand} from 'constants/MolbioConstants';
import {ModifiedNucleotideAttributes, SequenceType} from 'constants/SequenceConstants';
import {MonomerNaturalAnalogue} from 'graphql-client/__generated/graphqlGlobalTypes';
import {getUTFlexibleNaturalBasesIfEnabled, isFullUTFlexibilityEnabled} from 'modbio/util';
import {PRIMER_TEXT_MARGIN_RATIO} from 'molbio/maps/PrimersViewConstants';
import Type from 'molbio/maps/Type';
import {getNaturalMonomerId} from 'molbio/monomers/MonomersUtil';
import * as sequtil from 'molbio/SeqUtil';
import notifications from 'notifications';
import {DASHED_LINE, SOLID_LINE} from 'util/SVGUtil';

/**
 * Returns if the given sequence has selected two primers that can be made into a new primer pair
 */
export function canCreatePrimerPair(sequence: ComponentSequence): boolean {
  const selection = sequence.selection();
  const selectedPrimersAreCompatible = selection.canPCR();
  const selectedPrimers = [selection.getStartState('primer')!, selection.getEndState('primer')!];
  return (
    selectedPrimersAreCompatible &&
    !existingPrimerPairSelected(sequence) &&
    selectedPrimers.every((p) => !p.fromSubPart())
  );
}

/**
 * If a valid and unlinked primer pair has been selected, link them and open the links view.
 */
export function createPrimerPair(sequence: ComponentSequence, cleanup?: () => void): void {
  assert(canCreatePrimerPair(sequence), 'Selected primers cannot be linked');

  const forwardPrimer = sequence.selection().getStartState('primer')!;
  const reversePrimer = sequence.selection().getEndState('primer')!;

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const success = () => {
    sequence.updateVersions();
    sequence.get('selection').trigger('showLinks');
    notifications.success('Primers successfully linked.');
    analytics.track('primer', 'linked_primers', {sequence_id: sequence.get('id')});
  };
  const attrs = {primer_fwd_id: forwardPrimer.id, primer_rev_id: reversePrimer.id};
  sequence
    .primerPairs()
    .betterCreate(attrs)
    .fail(notifications.notifyError)
    .done(success)
    .always(() => {
      if (cleanup) {
        cleanup();
      }
    });
}

/**
 * Returns whether a sequence
 * 1) Has two primers selected and
 * 2) Those two primers are already linked in a PrimerPair
 *
 * Useful for determining if the user can create a new PrimerPair
 */
export function existingPrimerPairSelected(sequence: ComponentSequence): boolean {
  const primerPairSelected = sequence.selection().canPCR();
  let existingSelected;
  if (primerPairSelected) {
    const fwdPrimer = sequence.selection().getStartState('primer')!;
    const revPrimer = sequence.selection().getEndState('primer')!;
    existingSelected = Boolean(sequence.primerPairs().findPair(fwdPrimer, revPrimer));
  } else {
    existingSelected = false;
  }
  return existingSelected;
}

interface ModsByIndex {
  [index: number]: ModifiedNucleotideAttributes;
}
export function zippedBasesAndMods(
  bases: string,
  modifications: SequencePrimerAttributes['modifiedNucleotides']
): Array<{base: string; index: number; modification: ModifiedNucleotideAttributes | null}> {
  const modsByIndex: ModsByIndex = modifications.reduce(
    (modMap: ModsByIndex, mod: ModifiedNucleotideAttributes) => {
      modMap[mod.position] = mod;
      return modMap;
    },
    {}
  );
  return bases.split('').map((base, index) => ({
    base,
    index,
    modification: modsByIndex[index] || null,
  }));
}

export interface PrimerAttrs {
  start: number;
  end: number;
  strand: Strand;
  bases: string;
  overhangEdge: number | null;
  name: string;
  isNew: boolean;
}

export interface PrimerShapeOptions {
  start: number;
  end: number;
  sliceStart: number;
  sliceEnd: number;
  height: number;
  type: Type;
  sequenceLength: number;
  xFn(index: number): number;
  areBasesVisible: boolean;
  circular: boolean;
  sequenceDrawStart: number;
  sequenceDrawEnd: number;
  bindPosition: number;
  mismatches: Array<number>;
  dx: number;
  singleModificationHeight: number;
  modificationMarginHeight: number;
  totalModificationHeight: number;
  modifications: SequencePrimerAttributes['modifiedNucleotides'];
  nucleotideType: SequencePrimerAttributes['nucleotideType'];
}

interface ModificationProp {
  cx: number;
  cy: number;
  r: number;
  fill: string;
  shapeRendering: string;
  fillOpacity: number;
}

export interface TextProps {
  x: number;
  y: number;
  'font-size': number;
  transform: string;
  text: string;
  fill: string | undefined;
  baseProps: Array<{
    x: number;
    fill: string;
    text: string;
  }>;
  modificationProps: Array<ModificationProp>;
}

export interface PrimerShapeValues {
  strokeRightD: string;
  strokeRightRender: string;
  boxTransform: string;
  dashArray: string;
  strokeLeftD: string;
  strokeLeftRender: string;
  strokeTopD: string;
  strokeBottomD: string;
  strokeMiddleRender: string;
  fillD: string;
  overhangD: string;
  leftsideArrow: string;
  rightsideArrow: string;
  useTooltip: boolean;
  width: number;
  bodyHeight: number;
  showOverhang: boolean;
  textProps: TextProps | undefined;
  reverseBaselessBoxTransform: string;
}

/**
 * Hide the big ball of nuttiness away here. Should only do calculations, no DOM manipulation
 */
export function getPrimerShape(
  primer: PrimerAttrs,
  {
    start,
    end,
    height,
    type,
    sequenceLength,
    xFn,
    sliceStart,
    sliceEnd,
    areBasesVisible,
    circular,
    sequenceDrawEnd,
    sequenceDrawStart,
    bindPosition,
    mismatches,
    dx,
    singleModificationHeight,
    modificationMarginHeight,
    totalModificationHeight,
    modifications,
    nucleotideType,
  }: PrimerShapeOptions
): PrimerShapeValues {
  /**
   * Perform all the logic to determine how a primer is shaped, given all relevant factors. This helper was
   * extracted from PrimersView to be Backbone-agnostic, to be more easily testable and reusable, at the cost
   * of an absurd number of inputs and outputs. It assumes that the relevant elements are already created, and
   * leaves it up to the caller to actually set the attributes on the correct elements accordingly.
   *
   * Please see https://benchling.atlassian.net/wiki/spaces/MBMB/pages/241664030/Sequence+Map+Architecture+Overview for a
   * description of how the primer shape is constructed.
   */
  const boxOverlaps = sequtil.getRangeOverlap(start, end, primer.start, primer.end);
  const strand = primer.strand;
  const name = primer.name;
  const dashArray = primer.isNew ? DASHED_LINE : SOLID_LINE;
  const arrowsOffset = 6;
  const arrowWidth = 3;
  const arrowPadding = 3;
  const arrowSpacing = 3;

  let boxX: number;
  let width: number;
  let showLeftEnd;
  let showRightEnd;
  if (boxOverlaps.length === 0) {
    // There are no overlaps if we are rendering overhang.
    width = 0;
    showLeftEnd = false;
    showRightEnd = false;
    boxX = 0;
  } else {
    // Otherwise, we expect one overlap where the bound bases are.
    const [boxStart, boxEnd] = boxOverlaps[0];
    width = xFn(boxEnd) - xFn(boxStart);
    showLeftEnd = boxStart >= sliceStart;
    showRightEnd = boxEnd <= sliceEnd;
    boxX = xFn(boxStart) - xFn(start);
  }

  const displayBases = getUTFlexibleNaturalBasesIfEnabled(
    primer.bases,
    modifications,
    nucleotideType,
    SequenceType.OLIGO
  );
  const displayModifications = getModificationsForPrimerDisplay(modifications, nucleotideType);
  const showModifications = areBasesVisible && displayModifications.length > 0;

  // Give extra room above bases to show modifications
  const bodyHeight = showModifications ? type.height() + totalModificationHeight : type.height();

  // Adjust tip shape for modifications to have similar width to primers without modifications
  const tipWidth = showModifications ? _.min([height, width]) / 2 : _.min([height, width]);
  const thinTipHeight = showModifications ? (height - bodyHeight) / 2 : (height - bodyHeight) / 3;
  const thinTipWidth = showModifications ? tipWidth / 2 : tipWidth / 3;

  const jagWidth = areBasesVisible ? 2 : 4;
  const jagHeight = bodyHeight / 4;

  let strokeLeftRender = 'crispEdges';
  let strokeRightRender = 'crispEdges';
  const strokeMiddleRender = 'crispEdges';
  const boxTransform = `translate(${boxX}, 0)`;
  const overhangWidth = 16;
  const reverseBaselessBoxTransform = `translate(${width - overhangWidth + boxX}, 0)`;
  let showOverhang = false;

  let bottom;
  let fillD;
  let strokeBottomD;
  let strokeLeftD;
  let strokeRightD;
  let strokeTopD;
  let top;
  // Declare shape, including arrows, overhangs, and jagged edges when split
  if (width === 0) {
    // fucking https://github.com/mbostock/d3/issues/1424
    fillD = 'M 0 0';
    strokeLeftD = 'M 0 0';
    strokeTopD = 'M 0 0';
    strokeBottomD = 'M 0 0';
    strokeRightD = 'M 0 0';
  } else {
    fillD = '';
    strokeLeftD = '';
    strokeTopD = '';
    strokeBottomD = '';
    strokeRightD = '';
    if (showLeftEnd) {
      if (strand === -1) {
        // show left tip
        if (areBasesVisible) {
          strokeLeftD += `M ${thinTipWidth} ${bodyHeight} L ${tipWidth} ${
            height - thinTipHeight
          } L ${tipWidth} ${height} L 0 ${bodyHeight} L 0 0`;
        } else {
          strokeLeftD += `M ${tipWidth} ${bodyHeight} L ${tipWidth} ${height} L 0 0 `;
        }
        strokeTopD += `M 0 0 L ${tipWidth} 0`;
        if (areBasesVisible) {
          strokeBottomD += `M ${thinTipWidth} ${bodyHeight} `;
        } else {
          strokeBottomD += `M ${tipWidth} ${bodyHeight} `;
        }
        fillD += `${strokeLeftD}L ${tipWidth} 0 `;
        strokeLeftRender = 'geometricPrecision';
      } else {
        // show line
        bottom = height;
        top = height - bodyHeight;

        strokeLeftD += `M 0 ${bottom} L 0 ${top} `;
        strokeTopD += `M 0 ${top} `;
        strokeBottomD += `M 0 ${bottom} `;
        fillD += strokeLeftD;
        showOverhang = true;
      }
    } else {
      // show jagged line
      if (strand === 1) {
        bottom = height;
        top = height - bodyHeight;
      } else {
        bottom = bodyHeight;
        top = 0;
      }
      strokeLeftD += `M 0 ${bottom} L ${jagWidth} ${bottom - jagHeight} L 0 ${
        bottom - jagHeight * 2
      } L ${jagWidth} ${bottom - jagHeight * 3 + 1} L 0 ${top} `;
      strokeTopD += `M 0 ${top} `;
      strokeBottomD += `M 0 ${bottom} `;
      fillD += strokeLeftD;
      strokeLeftRender = 'geometricPrecision';
    }

    if (showRightEnd) {
      if (strand === 1) {
        // show right tip
        if (areBasesVisible) {
          strokeRightD += `M ${width} ${height} L ${width} ${height - bodyHeight} L ${width - tipWidth} 0 L ${
            width - tipWidth
          } ${thinTipHeight} L ${width - thinTipWidth} ${height - bodyHeight}`;
          strokeTopD += `L ${width - thinTipWidth} ${height - bodyHeight} `;
          fillD += `L ${width - thinTipWidth} ${height - bodyHeight} L ${
            width - tipWidth
          } ${thinTipHeight} L ${width - tipWidth} 0 L ${width} ${
            height - bodyHeight
          } L ${width} ${height} L 0 ${height} `;
        } else {
          strokeRightD += `M ${width} ${height} L ${width - tipWidth} 0 L ${width - tipWidth} ${
            height - bodyHeight
          } `;
          strokeTopD += `L ${width - tipWidth} ${height - bodyHeight} `;
          fillD += `L ${width - tipWidth} ${height - bodyHeight} L ${
            width - tipWidth
          } 0 L ${width} ${height} L 0 ${height} `;
        }
        strokeBottomD += `L ${width} ${height} `;
        strokeRightRender = 'geometricPrecision';
      } else {
        // show line
        strokeRightD += `M ${width} 0 L ${width} ${bodyHeight} `;
        strokeTopD += `L ${width} 0 `;
        strokeBottomD += `L ${width} ${bodyHeight} `;
        fillD += `L ${width} 0 L ${width} ${bodyHeight} `;
        showOverhang = true;
      }
    } else {
      // show jagged line
      if (strand === 1) {
        bottom = height;
        top = height - bodyHeight;
      } else {
        bottom = bodyHeight;
        top = 0;
      }
      strokeRightD += `M ${width} ${top} L ${width - jagWidth} ${bottom - jagHeight * 3 + 1} L ${width} ${
        bottom - jagHeight * 2
      } L ${width - jagWidth} ${bottom - jagHeight} L ${width} ${bottom} `;
      strokeTopD += `L ${width} ${top} `;
      strokeBottomD += `L ${width} ${bottom} `;
      fillD += `L ${width} ${top} L ${width - jagWidth} ${bottom - jagHeight * 3 + 1} L ${width} ${
        bottom - jagHeight * 2
      } L ${width - jagWidth} ${bottom - jagHeight} L ${width} ${bottom} `;
      strokeRightRender = 'geometricPrecision';
    }
  }

  // Arrows / Text

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const arrowD = (xoffset: number, dir: Strand) => {
    const moveY = strand === 1 ? height - bodyHeight : 0;
    const endXByStrand: {[key in Strand]: number} = {
      [-1]: xoffset + arrowWidth,
      0: xoffset,
      1: xoffset,
    };
    const endX = endXByStrand[dir];

    const tipXByStrand: {[key in Strand]: number} = {
      [-1]: xoffset,
      0: xoffset,
      1: xoffset + arrowWidth,
    };
    const tipX = tipXByStrand[dir];

    const topY = arrowPadding + 1 + moveY;
    const bottomY = bodyHeight - arrowPadding + moveY;
    const middleY = bodyHeight / 2 + moveY;
    return `M ${endX} ${topY} L ${tipX} ${middleY} L ${endX} ${bottomY} `;
  };

  let leftsideArrow;
  const noOpPath = 'M 0 0 L 0 0';
  // Show arrows only next to jaggies.
  if (showLeftEnd) {
    leftsideArrow = noOpPath;
  } else {
    leftsideArrow = arrowD(arrowsOffset, strand) + arrowD(arrowsOffset + arrowSpacing, strand);
  }

  let rightsideArrow;
  if (showRightEnd) {
    rightsideArrow = noOpPath;
  } else {
    rightsideArrow =
      arrowD(width - arrowWidth - arrowSpacing - arrowsOffset, strand) +
      arrowD(width - arrowWidth - arrowsOffset, strand);
  }

  const padding = arrowsOffset + arrowSpacing + arrowWidth;

  // is there room for at least one arrow?
  const noShowArrows = padding > width - tipWidth;

  // If you can see both sides, leave it alone.
  if (noShowArrows || (showRightEnd && showLeftEnd)) {
    leftsideArrow = noOpPath;
    rightsideArrow = noOpPath;
  }

  const useTooltip = type.width(name.length) + 2 * padding > width - tipWidth || areBasesVisible;

  const primerTextMargin = type.height() * PRIMER_TEXT_MARGIN_RATIO;
  let textHeight: number;
  if (strand === 1) {
    textHeight = height - primerTextMargin;
  } else {
    textHeight = bodyHeight - primerTextMargin;
  }

  let textProps: TextProps | undefined;

  if (areBasesVisible) {
    let drawEnd;
    let drawStart;

    let basesAndMods = zippedBasesAndMods(displayBases, displayModifications);
    if (strand === -1) {
      basesAndMods = basesAndMods.reverse();
    }

    if (circular) {
      [drawStart, drawEnd] = [sequenceDrawStart, sequenceDrawEnd];
    } else {
      // Allow overhangEdge to go off the sequence, so we can compute where primer bases should start.
      drawStart = primer.overhangEdge;
      drawEnd = bindPosition;
      if (strand === -1) {
        [drawStart, drawEnd] = [drawEnd, drawStart];
      }
    }

    const overlaps = sequtil.getRangeOverlap(sliceStart, sliceEnd, start, end);
    if (overlaps.length !== 0) {
      let strStart: number;
      const [baseStart, baseEnd] = overlaps[0];
      if (drawStart === baseStart) {
        strStart = 0;
      } else {
        assert(drawStart != null, 'Error calculating primer position with overhangs');
        strStart = sequtil.length(drawStart, baseStart, sequenceLength);
      }

      const strEnd = sequtil.length(drawStart, baseEnd, sequenceLength);
      basesAndMods = circularArraySlice(basesAndMods, strStart, strEnd);

      if (basesAndMods.length > 0) {
        const forwardModYCenter =
          height -
          bodyHeight +
          0.5 * primerTextMargin +
          modificationMarginHeight +
          0.5 * singleModificationHeight;
        const reverseModYCenter =
          0.5 * primerTextMargin + modificationMarginHeight + 0.5 * singleModificationHeight;
        const modificationProps = basesAndMods
          .map((baseObj, index): ModificationProp | null =>
            baseObj.modification != null
              ? {
                  cx: index * type.charWidth() + type.charWidth() / 2,
                  cy: strand === 1 ? forwardModYCenter : reverseModYCenter,
                  r: (1 / 2) * singleModificationHeight,
                  fill: '#7051FC', // todo extract and fix colors
                  shapeRendering: 'geometricPrecision',
                  fillOpacity: 1,
                }
              : null
          )
          .filter((mod: ModificationProp | null) => mod != null) as Array<ModificationProp>;

        textProps = {
          x: 0,
          y: textHeight,
          'font-size': type.fontSize,
          transform: '',
          text: '',
          fill: undefined,
          baseProps: basesAndMods.map((baseObj, index) => ({
            // Add letter spacing / 2 since we center letters above axis.
            x: index * type.charWidth() + type.letterSpacing / 2,
            fill: mismatches.includes(index + strStart) ? '#d9534f' : 'black', // mismatch bug here for rev primers?
            text: baseObj.base,
          })),
          modificationProps: BenchlingConfig.MOLBIO_ENABLE_MODIFIED_NUCLEOTIDES ? modificationProps : [],
        };
      }
    }
  } else {
    let altX = (width - tipWidth - type.width(name.length)) / 2;
    if (strand === -1) {
      altX += tipWidth;
    }
    if (strand === -1) {
      textHeight += 1;
    }
    textProps = {
      x: altX,
      y: textHeight,
      'font-size': type.fontSize,
      fill: 'black',
      text: useTooltip || dx === 0 ? '' : name,
      transform: boxTransform,
      baseProps: [],
      modificationProps: [], // Only show mods when bases are visible
    };
  }

  let overhangBottom;
  let overhangTop;
  let overhangD;
  if (primer.strand === 1) {
    overhangBottom = height;
    overhangTop = height - bodyHeight;
    const middle = (overhangBottom + overhangTop) / 2;
    overhangD = `M ${overhangWidth} ${overhangTop} L ${overhangWidth / 2} ${overhangTop} L 0 ${middle} L ${
      overhangWidth / 2
    } ${overhangBottom} L ${overhangWidth} ${overhangBottom} L ${
      (overhangWidth * 2) / 3
    } ${middle} L ${overhangWidth} ${overhangTop}`;
  } else {
    overhangD = `M 0 0 L ${overhangWidth / 2} 0 L ${overhangWidth} ${bodyHeight / 2} L ${
      overhangWidth / 2
    } ${bodyHeight} L 0 ${bodyHeight} L ${overhangWidth / 3} ${bodyHeight / 2} L 0 0`;
  }

  return {
    strokeRightD,
    strokeRightRender,
    boxTransform,
    dashArray,
    strokeLeftD,
    strokeLeftRender,
    strokeTopD,
    strokeBottomD,
    strokeMiddleRender,
    fillD,
    leftsideArrow,
    rightsideArrow,
    useTooltip,
    width,
    bodyHeight,
    showOverhang,
    textProps,
    reverseBaselessBoxTransform,
    overhangD,
  };
}

/**
 * Returns a new list of mods corresponding to those we want to display as dots above primer bases.
 * Excludes: 1) null phosphates; and 2) natural U/T mixed bases.
 */
export function getModificationsForPrimerDisplay(
  modifiedNucleotides: Readonly<Array<ModifiedNucleotideAttributes>>,
  nucleotideType: NucleotideType
): Array<ModifiedNucleotideAttributes> {
  if (!BenchlingConfig.MOLBIO_ENABLE_MODIFIED_NUCLEOTIDES || modifiedNucleotides.length === 0) {
    return [];
  }

  const naturalSugarId =
    nucleotideType === NucleotideType.DNA
      ? getNaturalMonomerId(MonomerNaturalAnalogue.D_LOWER)
      : getNaturalMonomerId(MonomerNaturalAnalogue.R_LOWER);
  const naturalPhosphateId = getNaturalMonomerId(MonomerNaturalAnalogue.P_LOWER);
  const mixedBase =
    nucleotideType === NucleotideType.DNA ? MonomerNaturalAnalogue.U : MonomerNaturalAnalogue.T;
  const naturalMonomerIds = Object.entries(BenchlingConfig.MODBIO_NATURAL_MONOMER_IDS_BY_NATURAL_ANALOGUE)
    .filter(([na, _]) => isFullUTFlexibilityEnabled() || na !== mixedBase)
    .map(([_, id]) => id);

  return modifiedNucleotides.filter(
    (mod) =>
      // This conditional filters out both null phosphates and natural mixed bases
      !(
        mod.sugarMonomerId === naturalSugarId &&
        naturalMonomerIds.includes(mod.baseMonomerId) &&
        (mod.phosphateMonomerId == null || mod.phosphateMonomerId === naturalPhosphateId)
      )
  );
}
